import { useMemo, useState, useRef, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { resolveAssetUrl, productPath } from '../config';
import { toast } from './Toast';
import { getHomeAccessories } from '../api/product.api';
import { isPlaceholderColor } from '../utils/placeholderColor';

const formatKES = (amount) =>
    'KES ' + Number(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 });

const TYPE_LABELS = {
    charger: 'Chargers',
    cable: 'Cables',
    cables: 'Cables',
    cover: 'Cases',
    'cover/case': 'Cover/Case',
    'charger/adapter': 'Charger/Adapter',
    audio: 'Audio',
    'screen protection': 'Screen Protection',
    'screen_protector': 'Screen Protectors',
    'power bank': 'Power Bank',
    powerbank: 'Power Banks',
    'storage/memory': 'Storage/Memory',
    'stylus/pen': 'Stylus/Pen',
    'holder/stand': 'Holder/Stand',
    'input device': 'Input Device',
    networking: 'Networking',
    earbuds: 'Earbuds',
    other: 'Other',
};

function tabLabel(tab) {
    if (tab === 'all') return 'All';
    return TYPE_LABELS[String(tab).toLowerCase()] || tab;
}

function resolveImage(image) {
    if (!image) return null;
    return resolveAssetUrl(image);
}

function AccessoryCard({ item, onAddToCart }) {
    const variants = item.variants || [];

    const initVar =
        variants.find((v) => v._id?.toString() === item.variantId?.toString()) ||
        variants[0];

    const initAttrs = {};
    for (const attr of initVar?.attributes || []) {
        if (attr.slug && attr.valueId) initAttrs[attr.slug] = attr.valueId;
    }

    const [selectedColorName, setSelectedColorName] = useState(
        initVar?.color?.name || null
    );
    const [selectedAttrs, setSelectedAttrs] = useState(initAttrs);
    const [adding, setAdding] = useState(false);
    const [added, setAdded] = useState(false);

    const uniqueColors = useMemo(() => {
        const seen = new Map();
        for (const v of variants) {
            if (v.color && !isPlaceholderColor(v.color) && !seen.has(v.color.name)) seen.set(v.color.name, v.color);
        }
        return [...seen.values()];
    }, [variants]);

    const attributeOptions = useMemo(() => {
        const colorVars = selectedColorName
            ? variants.filter((v) => v.color?.name === selectedColorName)
            : variants;
        const attrMap = new Map();
        for (const v of colorVars) {
            for (const attr of v.attributes || []) {
                if (!attrMap.has(attr.slug)) {
                    attrMap.set(attr.slug, {
                        name: attr.name,
                        slug: attr.slug,
                        values: new Map(),
                    });
                }
                attrMap
                    .get(attr.slug)
                    .values.set(attr.value, { value: attr.value, valueId: attr.valueId });
            }
        }
        return [...attrMap.values()].map((a) => ({
            ...a,
            values: [...a.values.values()],
        }));
    }, [variants, selectedColorName]);

    const selectedVariant = useMemo(() => {
        if (!variants.length) return null;
        let filtered = variants;
        if (selectedColorName)
            filtered = filtered.filter((v) => v.color?.name === selectedColorName);
        for (const [slug, valueId] of Object.entries(selectedAttrs)) {
            filtered = filtered.filter((v) =>
                v.attributes.some(
                    (a) => a.slug === slug && a.valueId === valueId
                )
            );
        }
        return filtered.find((v) => v.inStock) || filtered[0] || null;
    }, [variants, selectedColorName, selectedAttrs]);

    const handleColorSelect = (colorName) => {
        const colorVars = variants.filter((v) => v.color?.name === colorName);
        const firstVar = colorVars.find((v) => v.inStock) || colorVars[0];
        const newAttrs = {};
        for (const attr of firstVar?.attributes || []) {
            if (attr.slug && attr.valueId) newAttrs[attr.slug] = attr.valueId;
        }
        setSelectedColorName(colorName);
        setSelectedAttrs(newAttrs);
        setAdded(false);
    };

    const handleAttrSelect = (slug, valueId) => {
        setSelectedAttrs((prev) => ({ ...prev, [slug]: valueId }));
        setAdded(false);
    };

    const handleAdd = async () => {
        const varId = selectedVariant?._id || item.variantId;
        if (!varId || adding || added) return;
        setAdding(true);
        try {
            await onAddToCart(varId, 1);
            setAdded(true);
            toast.success(`${item.name} added to cart!`);
        } catch (err) {
            const msg = err?.response?.data?.message || 'Failed to add to cart';
            toast.error(msg);
        } finally {
            setAdding(false);
        }
    };

    const displayPrice =
        selectedVariant?.discountedPrice ?? selectedVariant?.price ?? item.price;
    const originalPrice = selectedVariant?.price ?? item.price;
    const hasDiscount = selectedVariant
        ? selectedVariant.discountedPrice != null &&
          selectedVariant.discountedPrice < selectedVariant.price
        : item.discountedPrice != null && item.discountedPrice < item.price;
    const imgSrc = resolveImage(selectedVariant?.image || item.image);
    const inStock = selectedVariant?.inStock ?? item.inStock;

    return (
        <div className="flex-shrink-0 w-44 sm:w-48 snap-start rounded-lg border border-border hover:border-primary/30 transition-colors p-2.5 sm:p-3 flex flex-col">
            <Link to={productPath(selectedVariant?.slug || item.variantSlug || item.slug)} className="block mb-2">
                <div className="w-full h-24 sm:h-28 rounded overflow-hidden flex items-center justify-center product-image-well">
                    {imgSrc ? (
                        <img
                            src={imgSrc}
                            alt={item.name}
                            className="w-full h-full object-contain"
                            loading="lazy"
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center">
                            <i className="ri-image-line text-muted-foreground/40 text-2xl"></i>
                        </div>
                    )}
                </div>
            </Link>

            <Link to={productPath(selectedVariant?.slug || item.variantSlug || item.slug)}>
                <p className="text-xs font-semibold text-foreground hover:text-primary transition-colors line-clamp-2 min-h-[2rem] mb-2">
                    {item.name}
                </p>
            </Link>

            {uniqueColors.length > 1 && (
                <div className="flex gap-1 flex-wrap mb-1.5">
                    {uniqueColors.map((color) => (
                        <button
                            key={color.name}
                            type="button"
                            title={color.name}
                            onClick={() => handleColorSelect(color.name)}
                            className={`w-4 h-4 rounded-full border-2 flex-shrink-0 transition-all ${
                                selectedColorName === color.name
                                    ? 'border-primary scale-110'
                                    : 'border-border hover:border-primary/60'
                            }`}
                            style={{ backgroundColor: color.hexCode || '#6b7280' }}
                        />
                    ))}
                </div>
            )}

            {attributeOptions.map((attr) =>
                attr.values.length > 1 ? (
                    <div key={attr.slug} className="flex gap-1 flex-wrap mb-1.5">
                        {attr.values.map((val) => (
                            <button
                                key={val.value}
                                type="button"
                                onClick={() => handleAttrSelect(attr.slug, val.valueId)}
                                className={`px-1.5 py-0.5 rounded text-[10px] font-medium border transition-colors ${
                                    selectedAttrs[attr.slug] === val.valueId
                                        ? 'bg-primary text-white border-primary'
                                        : 'border-border text-muted-foreground hover:border-primary/60'
                                }`}
                            >
                                {val.value}
                            </button>
                        ))}
                    </div>
                ) : null
            )}

            <div className="mt-auto pt-1 mb-2">
                {hasDiscount ? (
                    <div className="flex items-baseline gap-1">
                        <span className="text-xs font-bold text-primary">
                            {formatKES(displayPrice)}
                        </span>
                        <span className="text-[10px] text-muted-foreground line-through">
                            {formatKES(originalPrice)}
                        </span>
                    </div>
                ) : (
                    <span className="text-xs font-bold text-primary">
                        {formatKES(displayPrice)}
                    </span>
                )}
            </div>

            <button
                type="button"
                onClick={handleAdd}
                disabled={adding || added || !inStock}
                className={`w-full px-2 py-1.5 rounded-md text-xs font-semibold transition-all ${
                    added
                        ? 'bg-green-100 text-green-700 cursor-default'
                        : 'bg-primary text-white hover:brightness-110 disabled:opacity-50'
                }`}
            >
                {!inStock ? (
                    'Out of stock'
                ) : adding ? (
                    <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin inline-block"></span>
                ) : added ? (
                    <>
                        <i className="ri-check-line"></i> Added
                    </>
                ) : (
                    <>
                        <i className="ri-add-line"></i> Add
                    </>
                )}
            </button>
        </div>
    );
}

export default function CompatibleAccessories({
    items,
    types: typesProp,
    fetchOnTabChange = false,
    onAddToCart,
    title = 'Compatible Accessories',
    viewAllLink,
}) {
    const [activeTab, setActiveTab] = useState('all');
    const [tabItems, setTabItems] = useState(null);
    const [tabLoading, setTabLoading] = useState(false);
    const cacheRef = useRef(new Map());
    const carouselRef = useRef(null);

    const tabs = useMemo(() => {
        if (typesProp?.length) {
            return ['all', ...typesProp];
        }
        const present = [
            ...new Set((items || []).map((i) => i.accessoryType || 'other')),
        ];
        return ['all', ...present];
    }, [items, typesProp]);

    const loadType = useCallback(async (type) => {
        if (!fetchOnTabChange || type === 'all') {
            setTabItems(null);
            return;
        }
        if (cacheRef.current.has(type)) {
            setTabItems(cacheRef.current.get(type));
            return;
        }
        setTabLoading(true);
        try {
            const res = await getHomeAccessories({
                accessoryType: type,
                limit: 250,
            });
            const list = res?.data?.isOk
                ? (res.data.data?.items || []).filter((a) => a.inStock)
                : [];
            cacheRef.current.set(type, list);
            setTabItems(list);
        } catch (err) {
            console.error('Error loading accessory type:', err);
            setTabItems([]);
        } finally {
            setTabLoading(false);
        }
    }, [fetchOnTabChange]);

    useEffect(() => {
        // Reset type cache when the All sample changes
        cacheRef.current = new Map();
        setActiveTab('all');
        setTabItems(null);
    }, [items]);

    const handleTabClick = (tab) => {
        setActiveTab(tab);
        if (carouselRef.current) carouselRef.current.scrollLeft = 0;
        if (fetchOnTabChange) loadType(tab);
    };

    const visibleItems = useMemo(() => {
        if (activeTab === 'all' || !fetchOnTabChange) {
            if (activeTab === 'all') return items || [];
            return (items || []).filter(
                (i) => (i.accessoryType || 'other') === activeTab
            );
        }
        return tabItems || [];
    }, [items, activeTab, fetchOnTabChange, tabItems]);

    const scrollCarousel = (direction) => {
        if (!carouselRef.current) return;
        const scrollAmount = 220;
        carouselRef.current.scrollBy({
            left: direction === 'left' ? -scrollAmount : scrollAmount,
            behavior: 'smooth',
        });
    };

    if (!items || items.length === 0) return null;

    return (
        <div className="overflow-hidden">
            <div className="flex items-end justify-between gap-3 mb-3">
                <h3 className="font-display font-bold text-foreground text-xl sm:text-2xl tracking-tight truncate">
                    {title}
                </h3>
                {viewAllLink && (
                    <Link
                        to={viewAllLink}
                        className="text-sm font-semibold text-primary hover:underline shrink-0"
                    >
                        View All
                    </Link>
                )}
            </div>

            {tabs.length > 2 && (
                <div
                    className="flex gap-1.5 mb-4 overflow-x-auto scrollbar-hide pb-1"
                    style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                >
                    {tabs.map((tab) => (
                        <button
                            key={tab}
                            type="button"
                            onClick={() => handleTabClick(tab)}
                            className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[11px] font-medium transition-colors ${
                                activeTab === tab
                                    ? 'bg-primary text-white'
                                    : 'bg-surface border border-border text-muted-foreground hover:text-foreground hover:border-primary/30'
                            }`}
                        >
                            {tabLabel(tab)}
                        </button>
                    ))}
                </div>
            )}

            <div className="relative group overflow-hidden">
                {tabLoading ? (
                    <div className="flex gap-3 overflow-hidden">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <div
                                key={n}
                                className="flex-shrink-0 w-44 sm:w-48 h-52 rounded-lg bg-muted/60 animate-pulse"
                            />
                        ))}
                    </div>
                ) : (
                    <>
                        {visibleItems.length > 3 && (
                            <>
                                <button
                                    type="button"
                                    onClick={() => scrollCarousel('left')}
                                    className="absolute left-0 top-1/2 -translate-y-1/2 z-10 hidden sm:flex h-8 w-8 items-center justify-center rounded-full bg-background/90 border border-border shadow-sm text-foreground hover:bg-surface opacity-0 group-hover:opacity-100 transition-opacity"
                                    aria-label="Scroll left"
                                >
                                    <ChevronLeft className="h-4 w-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => scrollCarousel('right')}
                                    className="absolute right-0 top-1/2 -translate-y-1/2 z-10 hidden sm:flex h-8 w-8 items-center justify-center rounded-full bg-background/90 border border-border shadow-sm text-foreground hover:bg-surface opacity-0 group-hover:opacity-100 transition-opacity"
                                    aria-label="Scroll right"
                                >
                                    <ChevronRight className="h-4 w-4" />
                                </button>
                            </>
                        )}

                        <div
                            ref={carouselRef}
                            className="flex gap-3 overflow-x-auto scrollbar-hide scroll-smooth snap-x snap-mandatory"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {visibleItems.length === 0 ? (
                                <p className="text-sm text-muted-foreground py-8 px-2">
                                    No accessories in this category right now.
                                </p>
                            ) : (
                                visibleItems.map((item) => (
                                    <AccessoryCard
                                        key={item._id || item.variantId}
                                        item={item}
                                        onAddToCart={onAddToCart}
                                    />
                                ))
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}
